import { useState, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { theme } from '../../../theme';
import { fetchOrgVenues } from '../services/assessmentHistoryService';
import type { AssessmentFilters as FilterState, DatePreset, OutcomeKey, CaseStatus } from '../types/assessmentHistory.types';
import { CASE_STATUS_LABELS, CASE_STATUSES } from '../types/assessmentHistory.types';

type AssessmentFiltersProps = {
  filters: FilterState;
  onChange: (filters: FilterState) => void;
};

const OUTCOME_OPTIONS: { value: OutcomeKey | 'all'; label: string }[] = [
  { value: 'all', label: 'All Outcomes' },
  { value: 'emergency', label: 'Emergency' },
  { value: 'possible_concussion', label: 'Concussion' },
  { value: 'monitoring', label: 'Monitoring' },
  { value: 'no_signs', label: 'No Signs' },
];

const DATE_OPTIONS: { value: DatePreset; label: string }[] = [
  { value: 'all', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
];

export const AssessmentFiltersComponent = ({ filters, onChange }: AssessmentFiltersProps) => {
  const [venues, setVenues] = useState<{ id: string; venue_name: string }[]>([]);

  useEffect(() => {
    fetchOrgVenues().then(setVenues);
  }, []);

  const updateFilter = <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    onChange({ ...filters, [key]: value });
  };

  const hasActiveFilters = filters.outcome !== 'all' || filters.status !== 'all' || filters.datePreset !== 'all' || filters.venueId !== null;

  const clearFilters = () => {
    onChange({
      search: filters.search,
      outcome: 'all',
      status: 'all',
      datePreset: 'all',
      dateFrom: null,
      dateTo: null,
      venueId: null,
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Search */}
      <div className="relative flex-1 min-w-[180px] max-w-xs">
        <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${theme.text.muted}`} />
        <input
          type="text"
          placeholder="Search name or ref..."
          value={filters.search}
          onChange={(e) => updateFilter('search', e.target.value)}
          className={`${theme.input.base} pl-8 !py-1.5 !text-sm`}
        />
      </div>

      {/* Outcome dropdown */}
      <select
        value={filters.outcome}
        onChange={(e) => updateFilter('outcome', e.target.value as OutcomeKey | 'all')}
        className={`${theme.input.select} flex-1 !py-1.5 !text-sm !pr-8 min-w-[130px]`}
      >
        {OUTCOME_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>

      {/* Status dropdown */}
      <select
        value={filters.status}
        onChange={(e) => updateFilter('status', e.target.value as CaseStatus | 'all')}
        className={`${theme.input.select} flex-1 !py-1.5 !text-sm !pr-8 min-w-[150px]`}
      >
        <option value="all">All Statuses</option>
        {CASE_STATUSES.map((s) => (
          <option key={s} value={s}>{CASE_STATUS_LABELS[s]}</option>
        ))}
      </select>

      {/* Date dropdown */}
      <select
        value={filters.datePreset}
        onChange={(e) => updateFilter('datePreset', e.target.value as DatePreset)}
        className={`${theme.input.select} flex-1 !py-1.5 !text-sm !pr-8 min-w-[120px]`}
      >
        {DATE_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>

      {/* Venue dropdown */}
      {venues.length > 0 && (
        <select
          value={filters.venueId ?? ''}
          onChange={(e) => updateFilter('venueId', e.target.value || null)}
          className={`${theme.input.select} flex-1 !py-1.5 !text-sm !pr-8 min-w-[130px]`}
        >
          <option value="">All Venues</option>
          {venues.map((v) => (
            <option key={v.id} value={v.id}>{v.venue_name}</option>
          ))}
        </select>
      )}

      {/* Clear */}
      {hasActiveFilters && (
        <button
          onClick={clearFilters}
          className={`flex items-center gap-1 text-xs font-medium ${theme.text.accent} hover:underline px-1`}
        >
          <X className="w-3 h-3" /> Clear
        </button>
      )}
    </div>
  );
};
